import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Check, Palette, ShieldCheck, Zap } from "lucide-react";
import { getPostBySlug, listPublishedPosts } from "@/lib/blog.functions";
import { SiteHeader, SiteFooter } from "@/components/blog/SiteHeader";
import { BlogSidebar } from "@/components/blog/BlogSidebar";
import { markdownToPlainText, truncate } from "@/lib/blog/markdown";
import { Button } from "@/components/ui/button";
import { ExitIntentPopup } from "@/components/ExitIntentPopup";
import { MagicMyMessaging } from "@/components/MagicMyMessaging";

const bookingUrl = "/book";

const SITE_URL = "https://threedaylaunch.com";
const ORG_NAME = "Three Day Launch";
const ORG_LOGO = `${SITE_URL}/favicon.ico`;

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }) => {
    const [post, allPosts] = await Promise.all([
      getPostBySlug({ data: { slug: params.slug } }),
      listPublishedPosts({ data: {} }),
    ]);
    if (!post) throw notFound();
    const recentPosts = allPosts.filter((p) => p.slug !== params.slug).slice(0, 3);
    return { post, recentPosts };
  },
  notFoundComponent: () => (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-4xl font-bold text-foreground">Post not found</h1>
        <p className="mt-2 text-muted-foreground">This blog post doesn't exist or has been moved.</p>
        <Link
          to="/blog"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          Back to blog
        </Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-bold text-foreground">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      </div>
    </div>
  ),
  head: ({ loaderData, params }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [
          { title: "Post not found — Three Day Launch" },
          { name: "robots", content: "noindex, nofollow" },
        ],
      };
    }
    const url = `${SITE_URL}/blog/${post.slug}`;
    const description =
      post.meta_description ||
      post.excerpt ||
      truncate(markdownToPlainText(post.content), 160);
    const title = post.meta_title || `${post.title} — Three Day Launch`;
    const image = post.cover_image_url ?? undefined;

    const meta: { name?: string; property?: string; content: string; title?: string }[] = [
      { title } as never,
      { name: "description", content: description },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    if (image) {
      meta.push({ property: "og:image", content: image });
      meta.push({ name: "twitter:image", content: image });
    }
    const publishedAtIso = post.published_at
      ? new Date(post.published_at).getTime() > Date.now()
        ? new Date().toISOString()
        : post.published_at
      : null;
    if (publishedAtIso) {
      meta.push({ property: "article:published_time", content: publishedAtIso });
    }
    meta.push({ property: "article:modified_time", content: post.updated_at });
    meta.push({ property: "article:author", content: post.author_name });
    for (const t of post.tags) {
      meta.push({ property: "article:tag", content: t.name });
    }

    return {
      meta,
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description,
            image: image ? [image] : undefined,
            datePublished: publishedAtIso ?? undefined,
            dateModified: post.updated_at,
            author: { "@type": "Person", name: post.author_name },
            publisher: {
              "@type": "Organization",
              name: ORG_NAME,
              logo: { "@type": "ImageObject", url: ORG_LOGO },
            },
            mainEntityOfPage: { "@type": "WebPage", "@id": url },
            keywords: post.tags.map((t) => t.name).join(", ") || undefined,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
              { "@type": "ListItem", position: 3, name: post.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
});


function BlogPostPage() {
  const data = Route.useLoaderData();
  const post = data.post as import("@/lib/blog.server").BlogPostWithTags;
  const recentPosts = data.recentPosts as import("@/lib/blog.server").BlogPostWithTags[];

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-4 py-10 grid gap-10 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0 max-w-3xl">
        <nav aria-label="Breadcrumb" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex items-center gap-2">
            <li>
              <Link to="/" className="hover:text-foreground">Home</Link>
            </li>
            <li aria-hidden="true">/</li>
            <li>
              <Link to="/blog" className="hover:text-foreground">Blog</Link>
            </li>
          </ol>
        </nav>

        <article>
          <header className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-foreground md:text-5xl">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="mt-6 text-lg text-muted-foreground">{post.excerpt}</p>
            )}
          </header>

          {post.cover_image_url && (
            <figure className="mb-8 overflow-hidden rounded-xl">
              <img
                src={post.cover_image_url}
                alt={post.cover_image_alt ?? post.title}
                className="w-full object-cover"
              />
              {post.cover_image_alt && (
                <figcaption className="sr-only">{post.cover_image_alt}</figcaption>
              )}
            </figure>
          )}

          <section className="max-w-none text-foreground [&_a]:text-primary [&_a]:underline [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:my-1 [&_strong]:font-semibold [&_em]:italic [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_img]:my-6 [&_img]:rounded-lg">
            {(() => {
              const normalized = post.content.replace(/^(#{1,6})([^\s#])/gm, "$1 $2");
              const blocks = normalized.split(/\n{2,}/);
              const mid = Math.floor(blocks.length / 2);
              const first = blocks.slice(0, mid).join("\n\n");
              const second = blocks.slice(mid).join("\n\n");
              const mdComponents = {
                h1: ({ children }: { children?: React.ReactNode }) => <h1 className="mt-10 mb-4 text-5xl font-bold leading-tight">{children}</h1>,
                h2: ({ children }: { children?: React.ReactNode }) => <h2 className="mt-10 mb-4 text-4xl font-bold leading-tight">{children}</h2>,
                h3: ({ children }: { children?: React.ReactNode }) => <h3 className="mt-8 mb-3 text-3xl font-bold leading-snug">{children}</h3>,
                h4: ({ children }: { children?: React.ReactNode }) => <h4 className="mt-6 mb-2 text-2xl font-bold">{children}</h4>,
                h5: ({ children }: { children?: React.ReactNode }) => <h5 className="mt-6 mb-2 text-xl font-bold">{children}</h5>,
                h6: ({ children }: { children?: React.ReactNode }) => <h6 className="mt-6 mb-2 text-lg font-bold">{children}</h6>,
                p: ({ children }: { children?: React.ReactNode }) => (
                  <>
                    <p className="text-base leading-relaxed">{children}</p>
                    <br />
                  </>
                ),
                hr: () => (
                  <>
                    <hr className="my-6 border-border" />
                    <br />
                  </>
                ),
              };
              return (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {first}
                  </ReactMarkdown>
                  <div className="relative my-10 rounded-2xl bg-hero-gradient text-white p-8 md:p-10 shadow-card-soft text-center overflow-hidden border border-white/10">
                    <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/30 blur-3xl" />
                    <div className="pointer-events-none absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-highlight/20 blur-3xl" />
                    <div className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:18px_18px]" />
                    <div className="relative">
                      <div className="text-sm font-bold uppercase tracking-widest text-primary">Getting Started Is Easy</div>
                      <h3 className="mt-3 text-2xl md:text-3xl font-bold leading-tight">
                        Let's Launch Your <span className="text-primary">Strategic Coaching Website</span>
                      </h3>
                      <p className="mt-3 text-white/80 max-w-xl mx-auto">
                        Your first step is to book a free call so we can get your questions answered.
                      </p>
                      <Button asChild size="lg" className="mt-6 bg-primary-gradient hover:opacity-90 font-semibold h-12 px-7 rounded-full shadow-glow text-base">
                        <a href={bookingUrl} className="!text-white !no-underline">Book A Free Call <ArrowRight className="ml-2 h-4 w-4" /></a>
                      </Button>
                    </div>
                  </div>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                    {second}
                  </ReactMarkdown>
                </>
              );
            })()}
          </section>
        </article>

        <aside className="mt-12 rounded-xl border border-border bg-card p-6">
          <h2 className="text-lg font-semibold text-foreground">
            Ready to launch your own coaching website?
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            We design custom (no-template) websites for coaches like you.
          </p>
          <a
            href="/book"
            className="mt-4 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Get Started
          </a>
        </aside>

        {/* Crawler / no-JS fallback */}
        <noscript>
          <article>
            <h2>{post.title}</h2>
            
            {post.excerpt && <p>{post.excerpt}</p>}
            <div>{markdownToPlainText(post.content)}</div>
          </article>
        </noscript>
        </div>
        <BlogSidebar />
      </main>

      {recentPosts.length > 0 && (
        <section className="bg-muted/40 py-20">
          <div className="mx-auto max-w-7xl px-6">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">Read More</div>
              <h2 className="text-3xl md:text-4xl font-bold">
                The Latest In <span className="text-primary">Coaching Website Design</span>
              </h2>
            </div>
            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {recentPosts.map((p) => (
                <Link
                  key={p.slug}
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group bg-card rounded-2xl border border-border shadow-card-soft overflow-hidden hover:shadow-glow transition-shadow"
                >
                  {p.cover_image_url && (
                    <div className="aspect-video overflow-hidden bg-muted">
                      <img
                        src={p.cover_image_url}
                        alt={p.cover_image_alt ?? p.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{p.excerpt}</p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="relative overflow-hidden py-[45px]">
        <div className="mx-auto max-w-7xl px-6">
          <div className="relative rounded-3xl bg-hero-gradient text-white p-10 md:p-16 overflow-hidden shadow-card-soft">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-primary/30 blur-3xl" />
            <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-highlight/20 blur-3xl" />
            <div className="relative grid md:grid-cols-[1.4fr_1fr] gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-primary/15 border border-primary/30 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                  Custom design. No templates.
                </div>
                <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight">
                  Custom <span className="text-primary">coaching websites</span> built in just <span className="text-primary">3 days.</span>
                </h2>
                <p className="mt-5 text-white/75 text-lg max-w-xl">
                  You shouldn't have to wait for weeks or pay thousands for a coach website that works.
                </p>
                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                  {[
                    { icon: Zap, label: "Live in 3 days" },
                    { icon: Palette, label: "100% custom design" },
                    { icon: ShieldCheck, label: "Strategic, multi-page sites" },
                  ].map(({ icon: Icon, label }) => (
                    <div key={label} className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                      <Icon className="h-5 w-5 text-primary" />
                      <span className="text-sm font-semibold">{label}</span>
                    </div>
                  ))}
                </div>
                <Button asChild size="lg" className="mt-8 bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
                  <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
              </div>
              <div className="relative">
                <div className="bg-white text-foreground rounded-3xl p-8 pb-12 shadow-2xl rotate-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">THE SIGNATURE COACH</div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-primary">3 Day Launch®</span>
                  </div>
                  <div className="mt-4 h-px bg-border" />
                  <ul className="mt-4 space-y-2 text-sm">
                    {["Mobile Responsive Website", "SSL Certificate (HTTPS)", "SEO (Search Engine Optimization)", "Launched In 3 Days", "Ongoing Support"].map((i) => (
                      <li key={i} className="flex items-center gap-2"><Check className="h-4 w-4 text-primary" /> {i}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pt-12 pb-24 md:py-24 bg-muted/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">Our Trademarked Process</div>
            <h2 className="text-4xl md:text-5xl font-bold">Three days, <span className="text-primary">and you're live</span></h2>
            <p className="mt-4 text-muted-foreground text-lg">From "let's do it" to "we're live" in less time than most designers take to send a proposal.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Meet over Zoom for your Strategy Call.", d: "One on one with your designer to get clear on your vision and website goals" },
              { n: "02", t: "We design your custom website, all in one day.", d: "No meetings this day, you will just need to be available to provide feedback notes" },
              { n: "03", t: "Domain connection & launch celebration.", d: "We hit publish and connect your domain (like yourbusiness.com) and you're officially online!" },
            ].map((s) => (
              <div key={s.n} className="bg-card rounded-3xl p-8 border border-border shadow-card-soft">
                <div className="text-6xl font-bold text-primary/50">{s.n}</div>
                <h3 className="mt-2 text-xl font-bold">{s.t}</h3>
                <p className="mt-3 text-muted-foreground">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
              <a href={bookingUrl}>Get A Website That WORKS</a>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="mx-auto max-w-5xl px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold">
            Your coach website. <span className="text-primary">Live in 3 days.</span>
          </h2>
          <p className="mt-5 text-white/70 text-lg max-w-xl mx-auto">
Let's build a website that grows your business.
            <br /><br />
            Get started by scheduling a free, no-pressure call.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Button asChild size="lg" className="bg-primary-gradient hover:opacity-90 text-primary-foreground font-semibold min-h-14 h-auto py-3 px-6 sm:px-8 rounded-full shadow-glow text-base max-w-full whitespace-normal text-center">
              <a href={bookingUrl}>Start Today <ArrowRight className="ml-2 h-4 w-4" /></a>
            </Button>
          </div>
        </div>
      </section>

      <MagicMyMessaging />
      <SiteFooter />
      <ExitIntentPopup />
    </div>
  );
}
